import { weekFileName } from '../../lib/weekFileName';
import { currentDocLang } from '../../lib/docLang';
import { DocLangToggle } from '../ui/doc-lang-toggle';
import { useTranslation } from 'react-i18next';
import { WeekFilter } from '../ui/week-filter';
import { matchesWeekAny } from '../../lib/weekFilter';
import i18n from '../../i18n';
import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Plus, Search, FileText, Upload, Download, Edit, Trash2, 
  Eye, Clock, CheckCircle, AlertCircle, Filter, History,
  Link2, FolderOpen, Users, ChevronRight, CheckSquare, Square, FileStack
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import EntityLinker, { EntityLinkBadge } from './EntityLinker';
import { DocumentDetailDialog } from './ISODetailDialogs';
import useDeepShortcut from '../../hooks/useDeepShortcut';
import usePermissions from '../../hooks/usePermissions';
import { DateText } from '../ui/week-badge';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const DOC_STATUS = [
  { value: 'draft', label: i18n.t('auto.lbl.draft'), color: 'bg-gray-100 text-gray-800' },
  { value: 'pending_review', label: i18n.t('auto.lbl.pendingReview'), color: 'bg-yellow-100 text-yellow-800' },
  { value: 'pending_approval', label: i18n.t('auto.lbl.pendingApproval'), color: 'bg-blue-100 text-blue-800' },
  { value: 'approved', label: i18n.t('auto.lbl.approved'), color: 'bg-green-100 text-green-800' },
  { value: 'obsolete', label: i18n.t('auto.lbl.obsolete'), color: 'bg-red-100 text-red-800' },
  { value: 'archived', label: i18n.t('auto.lbl.archived'), color: 'bg-purple-100 text-purple-800' },
];

const DocumentRepository = ({ documents, employees, projects, onRefresh, token, docCategories = [], currentUser }) => {
  const { t } = useTranslation();
  const DOC_CATEGORIES = docCategories;
  // Rights come from the permissions matrix (ISO Documents row).
  const { can } = usePermissions();
  const canCreate = can('iso_documents.create');
  const canEdit = can('iso_documents.edit');
  const canDelete = can('iso_documents.delete');
  const [searchTerm, setSearchTerm] = useState('');
  const [weekFilter, setWeekFilter] = useState(null);
  // An auditor asks for several documents at once, so they can be ticked
  // off here and issued as one pack.
  const [packIds, setPackIds] = useState(new Set());
  const [packing, setPacking] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const togglePack = (id) => setPackIds((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    document_number: '',
    title: '',
    category: 'procedure',
    custom_category: '',
    description: '',
    url: '',
    reviewer_id: '',
    approver_id: '',
    review_frequency_months: 12,
    access_level: 'all',
    department: '',
    linked_project_ids: [],
    linked_employee_ids: [],
    tags: [],
  });

  const [uploadData, setUploadData] = useState({
    version: '',
    change_description: '',
    file: null,
  });

  const [showDetailDialog, setShowDetailDialog] = useState(false);

  // Dashboard shortcuts: pin the open controlled document.
  useDeepShortcut({
    active: showDetailDialog && !!selectedDocument,
    tab: 'iso9001',
    context: { isoSubTab: 'documents', focusDocId: selectedDocument?.id },
    label: selectedDocument?.document_number || selectedDocument?.title,
    subtitle: selectedDocument?.title || 'ISO 9001',
    icon: 'reports',
    perms: ['iso_documents.view'],
    priority: 3,
    focusKey: 'focusDocId',
    onFocus: (id) => {
      const found = (documents || []).find((d) => d.id === id);
      if (found) { setSelectedDocument(found); setShowDetailDialog(true); }
    },
  });

  // Fetch next auto-number for documents
  const fetchNextNumber = useCallback(async (category = 'procedure') => {
    try {
      const response = await fetch(
        `${API_BASE}/api/settings/next-number/document?category=${category}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        return data.next_number;
      }
    } catch (error) {
      console.error('Error fetching next number:', error);
    }
    return '';
  }, [token]);

  // Handle opening create dialog with auto-number
  const handleOpenCreateDialog = async () => {
    const nextNumber = await fetchNextNumber(formData.category);
    setFormData(prev => ({
      ...prev,
      document_number: nextNumber || prev.document_number,
      title: '',
      description: '',
      url: '',
      reviewer_id: '',
      approver_id: '',
      review_frequency_months: 12,
      access_level: 'all',
      department: '',
      linked_project_ids: [],
      linked_employee_ids: [],
      tags: [],
    }));
    setShowCreateDialog(true);
  };

  // Update document number when category changes
  const handleCategoryChange = async (newCategory) => {
    const nextNumber = await fetchNextNumber(newCategory);
    setFormData(prev => ({
      ...prev,
      category: newCategory,
      document_number: nextNumber || prev.document_number
    }));
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          doc.document_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || doc.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || doc.status === filterStatus;
    return matchesSearch && matchesCategory && matchesStatus && matchesWeekAny(weekFilter, [doc.next_review_date, doc.updated_at, doc.created_at]);
  });

  const buildPack = async () => {
    setPacking(true);
    try {
      const resp = await fetch(`${API_BASE}/api/iso/documents/pack/pdf`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ doc_ids: Array.from(packIds), lang: currentDocLang() }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `Pack failed (${resp.status})`);
      }
      const url = URL.createObjectURL(await resp.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = weekFileName(`controlled-copy-pack-${new Date().toISOString().slice(0, 10)}.pdf`);
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success(`Pack built from ${packIds.size} document(s)`);
      setPackIds(new Set());
    } catch (e) {
      toast.error(e.message);
    }
    setPacking(false);
  };

  const handleDownloadVersion = async (docId, version, fallbackName = 'document') => {
    toast.info(t('auto.iso.downloading'));
    try {
      const resp = await fetch(`${API_BASE}/api/iso/documents/${docId}/download/${version}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `Download failed (${resp.status})`);
      }
      const blob = await resp.blob();
      const disposition = resp.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : fallbackName;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = weekFileName(filename);
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleCreate = async () => {
    if (!formData.document_number || !formData.title) {
      toast.error(t('auto.iso.documentNumberAndTitleAreRequired'));
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/iso/documents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create document');
      }

      // Increment the auto-number for next record
      try {
        await fetch(`${API_BASE}/api/settings/increment-number/document`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (err) {
        console.error('Error incrementing number:', err);
      }

      toast.success(t('auto.iso.documentCreatedSuccessfully'));
      setShowCreateDialog(false);
      setFormData({
        document_number: '',
        title: '',
        category: 'procedure',
        custom_category: '',
        description: '',
        url: '',
        reviewer_id: '',
        approver_id: '',
        review_frequency_months: 12,
        access_level: 'all',
        department: '',
        linked_project_ids: [],
        linked_employee_ids: [],
        tags: [],
      });
      onRefresh();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadData.file || !uploadData.version) {
      toast.error(t('auto.iso.fileAndVersionAreRequired'));
      return;
    }

    setIsLoading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', uploadData.file);
      formDataUpload.append('version', uploadData.version);
      if (uploadData.change_description) {
        formDataUpload.append('change_description', uploadData.change_description);
      }

      const response = await fetch(`${API_BASE}/api/iso/documents/${selectedDocument.id}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formDataUpload,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload file');
      }

      toast.success(t('auto.iso.fileUploadedSuccessfully'));
      setShowUploadDialog(false);
      setUploadData({ version: '', change_description: '', file: null });
      setSelectedDocument(null);
      onRefresh();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (docId, newStatus) => {
    try {
      const response = await fetch(`${API_BASE}/api/iso/documents/${docId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) throw new Error('Failed to update status');
      toast.success(t('auto.iso.statusUpdated'));
      onRefresh();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm(t('auto.iso.areYouSureYouWantTo2'))) return;

    try {
      const response = await fetch(`${API_BASE}/api/iso/documents/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to delete document');
      toast.success(t('auto.iso.documentDeleted'));
      onRefresh();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getStatusBadge = (status) => {
    const statusInfo = DOC_STATUS.find(s => s.value === status);
    return (
      <Badge className={statusInfo?.color || 'bg-gray-100'}>
        {statusInfo?.label || status}
      </Badge>
    );
  };

  const getCategoryLabel = (category) => {
    const cat = DOC_CATEGORIES.find(c => c.value === category);
    return cat?.label || category;
  };

  return (
    <div className="space-y-4" data-testid="document-repository">
      {packIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2"
          data-testid="pack-bar">
          <span className="text-sm font-medium">{packIds.size} selected</span>
          <DocLangToggle testId="doc-lang-documentrepository-0" />
          <Button variant="outline" size="sm" onClick={buildPack} disabled={packing}
            data-testid="build-pack-btn">
            <FileStack className="mr-1 h-4 w-4" />
            {packing ? t('auto.iso.building') : t('auto.iso.controlledCopyPack')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setPackIds(new Set())}
            data-testid="pack-clear-btn">
            {t('auto.iso.clear')}
          </Button>
          <span className="text-xs text-muted-foreground">
            {t('auto.iso.onePdfContentsPageFirstEach')}
          </span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row gap-4 justify-between">
        <div className="flex flex-1 flex-wrap gap-2">
          <div className="relative min-w-[180px] flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder={t('auto.iso.searchDocuments')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <WeekFilter value={weekFilter} onChange={setWeekFilter} testId="documents-week-filter" />
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[160px] max-w-full">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder={t('auto.iso.category')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('auto.iso.allCategories')}</SelectItem>
              {DOC_CATEGORIES.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px] max-w-full">
              <SelectValue placeholder={t('auto.iso.status')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('auto.iso.allStatus')}</SelectItem>
              {DOC_STATUS.map(status => (
                <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {canCreate && (
          <Button onClick={handleOpenCreateDialog} data-testid="create-document-btn">
            <Plus className="h-4 w-4 mr-2" />
            {t('auto.iso.newDocument')}
          </Button>
        )}
      </div>

      {/* Documents List */}
      <div className="grid gap-4">
        {filteredDocuments.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>{t('auto.iso.noDocumentsFound')}</p>
            </CardContent>
          </Card>
        ) : (
          filteredDocuments.map(doc => (
            <Card 
              key={doc.id} 
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => {
                setSelectedDocument(doc);
                setShowDetailDialog(true);
              }}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <button
                    role="checkbox"
                    aria-checked={packIds.has(doc.id)}
                    aria-label={`Add ${doc.document_number} to the controlled copy pack`}
                    className="mt-0.5 shrink-0"
                    onClick={(e) => { e.stopPropagation(); togglePack(doc.id); }}
                    data-testid={`pack-select-${doc.document_number}`}
                  >
                    {packIds.has(doc.id)
                      ? <CheckSquare className="h-4 w-4 text-blue-600" />
                      : <Square className="h-4 w-4 text-gray-400" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                      <FileText className="h-4 w-4 text-blue-600" />
                      <span className="font-mono text-sm text-gray-500">{doc.document_number}</span>
                      {getStatusBadge(doc.status)}
                      <Badge variant="outline">{getCategoryLabel(doc.category)}</Badge>
                    </div>
                    <h3 className="font-medium">{doc.title}</h3>
                    {doc.description && (
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">{doc.description}</p>
                    )}
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400">
                      <span>Version: {doc.current_version}</span>
                      {doc.next_review_date && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Review: {<DateText value={doc.next_review_date} />}
                        </span>
                      )}
                      {doc.versions?.length > 0 && (
                        <span>{doc.versions.length} version(s)</span>
                      )}
                    </div>
                    {/* Linked entities */}
                    {(doc.linked_project_ids?.length > 0 || doc.linked_employee_ids?.length > 0) && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {doc.linked_project_ids?.slice(0, 2).map(id => {
                          const proj = projects.find(p => p.id === id);
                          return proj ? (
                            <EntityLinkBadge key={id} type="project" name={proj.name} />
                          ) : null;
                        })}
                        {doc.linked_project_ids?.length > 2 && (
                          <Badge variant="outline" className="text-xs">+{doc.linked_project_ids.length - 2} more</Badge>
                        )}
                        {doc.linked_employee_ids?.slice(0, 2).map(id => {
                          const emp = employees.find(e => e.id === id);
                          return emp ? (
                            <EntityLinkBadge key={id} type="employee" name={emp.name} />
                          ) : null;
                        })}
                        {doc.linked_employee_ids?.length > 2 && (
                          <Badge variant="outline" className="text-xs">+{doc.linked_employee_ids.length - 2} more</Badge>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
                    {doc.versions?.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleDownloadVersion(doc.id, doc.current_version, `${doc.document_number || 'document'}.pdf`); }}
                        data-testid={`download-doc-${doc.id}`}
                        title={t('auto.iso.downloadCurrentVersion')}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    )}
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDocument(doc);
                          setUploadData(prev => ({ ...prev, version: doc.current_version  }));
                          setShowUploadDialog(true);
                        }}
                        title={t('auto.iso.uploadNewVersion')}
                      >
                        <Upload className="h-4 w-4" />
                      </Button>
                    )}
                    {canEdit && doc.status === 'draft' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleStatusChange(doc.id, 'pending_review'); }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    )}
                    {canEdit && doc.status === 'pending_review' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleStatusChange(doc.id, 'pending_approval'); }}
                      >
                        <CheckCircle className="h-4 w-4" />
                      </Button>
                    )}
                    {canEdit && doc.status === 'pending_approval' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-green-600"
                        onClick={(e) => { e.stopPropagation(); handleStatusChange(doc.id, 'approved'); }}
                      >
                        <CheckCircle className="h-4 w-4" />
                      </Button>
                    )}
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600"
                        onClick={(e) => { e.stopPropagation(); handleDelete(doc.id); }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Document Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('auto.iso.createNewDocument')}</DialogTitle>
            <DialogDescription className="sr-only">{t('auto.iso.createANewIsoDocument')}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('auto.iso.documentNumber')}</label>
                <Input
                  value={formData.document_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, document_number: e.target.value  }))}
                  placeholder={t('auto.iso.autoGeneratedBasedOnCategory')}
                  data-testid="document-number-input"
                />
                <p className="text-xs text-gray-500 mt-1">{t('auto.iso.autoFilledBasedOnCategorySelection')}</p>
              </div>
              <div>
                <label className="text-sm font-medium">{t('auto.iso.category2')}</label>
                <Select
                  value={formData.category}
                  onValueChange={handleCategoryChange}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DOC_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">{t('auto.iso.title')}</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value  }))}
                placeholder={t('auto.iso.documentTitle')}
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('auto.iso.description')}</label>
              <textarea
                className="w-full min-h-[80px] p-2 border rounded-md text-sm"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value  }))}
                placeholder={t('auto.iso.documentDescription')}
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('auto.iso.externalUrl')}</label>
              <Input
                value={formData.url}
                onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value  }))}
                placeholder="https://example.com/document-reference"
                data-testid="document-url-input"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('auto.iso.reviewer')}</label>
                <Select
                  value={formData.reviewer_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, reviewer_id: value  }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('auto.iso.selectReviewer')} />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">{t('auto.iso.approver')}</label>
                <Select
                  value={formData.approver_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, approver_id: value  }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('auto.iso.selectApprover')} />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('auto.iso.reviewFrequencyMonths')}</label>
                <Input
                  type="number"
                  value={formData.review_frequency_months}
                  onChange={(e) => setFormData(prev => ({ ...prev, review_frequency_months: parseInt(e.target.value) || 12  }))}
                  min="1"
                  max="60"
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('auto.iso.accessLevel')}</label>
                <Select
                  value={formData.access_level}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, access_level: value  }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('auto.iso.allUsers')}</SelectItem>
                    <SelectItem value="managers">{t('auto.iso.managersOnly')}</SelectItem>
                    <SelectItem value="admins">{t('auto.iso.adminsOnly')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Entity Linking */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                <Link2 className="h-4 w-4" />
                {t('auto.iso.linkToProjectsEmployees')}
              </h4>
              <EntityLinker
                linkedProjectIds={formData.linked_project_ids}
                linkedEmployeeIds={formData.linked_employee_ids}
                projects={projects}
                employees={employees}
                onProjectsChange={(ids) => setFormData(prev => ({ ...prev, linked_project_ids: ids  }))}
                onEmployeesChange={(ids) => setFormData(prev => ({ ...prev, linked_employee_ids: ids  }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} data-testid="dialog-cancel-btn">{t('auto.iso.cancel')}</Button>
            <Button onClick={handleCreate} disabled={isLoading} data-testid="dialog-create-btn">
              {isLoading ? t('auto.iso.creating') : t('auto.iso.createDocument')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload File Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('auto.iso.uploadDocumentVersion')}</DialogTitle>
            <DialogDescription className="sr-only">{t('auto.iso.uploadANewVersionOfThe')}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <label className="text-sm font-medium">{t('auto.iso.document')}</label>
              <p className="text-sm text-gray-500">{selectedDocument?.document_number} - {selectedDocument?.title}</p>
            </div>
            <div>
              <label className="text-sm font-medium">{t('auto.iso.version')}</label>
              <Input
                value={uploadData.version}
                onChange={(e) => setUploadData(prev => ({ ...prev, version: e.target.value  }))}
                placeholder="e.g., 1.0, 1.1, 2.0"
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('auto.iso.changeDescription')}</label>
              <textarea
                className="w-full min-h-[60px] p-2 border rounded-md text-sm"
                value={uploadData.change_description}
                onChange={(e) => setUploadData(prev => ({ ...prev, change_description: e.target.value  }))}
                placeholder={t('auto.iso.whatChangedInThisVersion')}
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('auto.iso.file')}</label>
              <Input
                type="file"
                onChange={(e) => setUploadData(prev => ({ ...prev, file: e.target.files[0]  }))}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)} data-testid="dialog-cancel-btn">{t('auto.iso.cancel')}</Button>
            <Button onClick={handleUpload} disabled={isLoading} data-testid="dialog-upload-btn">
              {isLoading ? t('auto.iso.uploading') : t('auto.iso.upload')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Detail Dialog */}
      <DocumentDetailDialog
        open={showDetailDialog}
        onOpenChange={setShowDetailDialog}
        document={selectedDocument}
        employees={employees}
        projects={projects}
        token={token}
        onSave={() => onRefresh?.()}
        onRefresh={() => onRefresh?.()}
        docCategories={DOC_CATEGORIES}
      />
    </div>
  );
};

export default DocumentRepository;
